import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, ShieldAlert, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { GLOBAL_ROLES, TABS } from "./security/shared";
import { OverviewTab, TeamTab, ClientUsersTab, RolesMatrixTab, AssignmentsTab, AuditTab } from "./security/tabs";
import { CreateUserModal, EditUserModal, RoleModal, TeamRoleModal, ClientScopeModal, AssignCompanyModal, ClientRightsModal, ConfirmModal, TempPwModal } from "./security/modals";

export default function Security() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = TABS.some((tb) => tb.key === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const [tab, setTab] = useState(initialTab);
  const changeTab = (key) => { setTab(key); setSearchParams(key === "overview" ? {} : { tab: key }, { replace: true }); };
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState(null);
  const [clients, setClients] = useState([]);
  const [log, setLog] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [busy, setBusy] = useState(null);
  const [menu, setMenu] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [roleUser, setRoleUser] = useState(null);
  const [teamRoleUser, setTeamRoleUser] = useState(null);
  const [scopeUser, setScopeUser] = useState(null);
  const [assignUser, setAssignUser] = useState(null);
  const [rightsUser, setRightsUser] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [tempPw, setTempPw] = useState(null);

  const isSuper = me?.role === "super_admin";

  useEffect(() => {
    const q = searchParams.get("tab");
    if (q && TABS.some((tb) => tb.key === q)) setTab(q);
  }, [searchParams]);

  const loadAll = async () => {
    await Promise.allSettled([
      api.get("/admin/security-overview").then((r) => setOv(r.data)),
      api.get("/admin/users").then((r) => setUsers(r.data)),
      api.get("/admin/security-log").then((r) => setLog(r.data)),
      api.get("/clients").then((r) => setClients(r.data)),
      api.get("/admin/team-roles").then((r) => setMatrix(r.data)),
    ]);
    setLastRefreshed(new Date());
  };
  useEffect(() => { if (isSuper) loadAll(); }, [isSuper]);

  const act = async (id, fn, okMsg) => {
    setBusy(id); setMenu(null);
    try { const res = await fn(); if (okMsg) toast.success(t(okMsg)); loadAll(); return res; }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(null); }
  };

  const actions = {
    edit: (u) => { setMenu(null); setEditUser(u); },
    role: (u) => { setMenu(null); setRoleUser(u); },
    teamRole: (u) => { setMenu(null); setTeamRoleUser(u); },
    scope: (u) => { setMenu(null); setScopeUser(u); },
    assignCompany: (u) => { setMenu(null); setAssignUser(u); },
    rights: (u) => { setMenu(null); setRightsUser(u); },
    lock: (u) => setConfirm({ title: t("Konto sperren"), msg: t("Konto wirklich sperren? Der Benutzer kann sich nicht mehr anmelden."), label: t("Konto sperren"), tone: "red",
      onYes: () => act(u.id, () => api.post(`/admin/users/${u.id}/lock`), "Konto gesperrt") }),
    unlock: (u) => act(u.id, () => api.post(`/admin/users/${u.id}/unlock`), "Konto entsperrt"),
    reset2fa: (u) => setConfirm({ title: t("2FA zurücksetzen"), msg: t("2FA für diesen Benutzer zurücksetzen? Die Einrichtung muss neu erfolgen."), label: t("2FA zurücksetzen"), tone: "amber",
      onYes: () => act(u.id, () => api.post(`/users/${u.id}/reset-2fa`), "2FA zurückgesetzt") }),
    resetPw: (u) => setConfirm({ title: t("Passwort zurücksetzen"), msg: t("Neues temporäres Passwort erzeugen? Das alte Passwort wird ungültig."), label: t("Passwort zurücksetzen"), tone: "amber",
      onYes: async () => { const r = await act(u.id, () => api.post(`/admin/users/${u.id}/reset-password`), "Passwort zurückgesetzt"); if (r?.data?.temp_password) setTempPw({ email: u.email, password: r.data.temp_password, emailSent: false }); } }),
    resend: (u) => setConfirm({ title: t("Einladung erneut senden"), msg: t("Einladung mit neuem Zugang erneut senden?"), label: t("Einladung erneut senden"), tone: "blue",
      onYes: async () => { const r = await act(u.id, () => api.post(`/admin/users/${u.id}/resend-invite`), "Einladung gesendet"); if (r?.data?.temp_password) setTempPw({ email: u.email, password: r.data.temp_password, emailSent: r.data.email_sent }); } }),
    delete: (u) => setConfirm({ title: t("Löschen"), msg: t("Benutzer endgültig löschen? Dies kann nicht rückgängig gemacht werden."), label: t("Löschen"), tone: "red",
      onYes: () => act(u.id, () => api.delete(`/users/${u.id}`), "Benutzer gelöscht") }),
  };

  const teamUsers = useMemo(() => (users || []).filter((u) => GLOBAL_ROLES.includes(u.role)), [users]);
  const clientUsers = useMemo(() => (users || []).filter((u) => u.role === "client" || u.role === "client_staff"), [users]);

  if (!isSuper) {
    return (
      <div className="fade-in w-full" data-testid="security-page">
        <PageHeader title={t("Benutzer & Sicherheit")} subtitle={t("Security & User Management Center für Super Admins — echte Live-Daten, strikte Rollenlogik.")} />
        <Card className="p-10 text-center" data-testid="security-no-access">
          <ShieldAlert className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h3 className="font-bold text-lg">{t("Nur für Super Admins")}</h3>
          <p className="text-sm text-gray-400 mt-1">{t("Diese Seite ist nur für Super Admins zugänglich.")}</p>
        </Card>
      </div>
    );
  }

  const ctx = { t, nav, me, ov, users, clients, log, matrix, teamUsers, clientUsers, busy, menu, setMenu, actions, setCreateOpen, setTab: changeTab, lastRefreshed, reload: loadAll };

  return (
    <div className="fade-in w-full" data-testid="security-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <PageHeader title={t("Benutzer & Sicherheit")} subtitle={t("Security & User Management Center für Super Admins — echte Live-Daten, strikte Rollenlogik.")} />
        <div className="flex items-center gap-2 mt-1 shrink-0">
          {lastRefreshed && <span className="text-[11px] text-gray-500 hidden md:block mr-1" data-testid="sec-last-refreshed">{t("Zuletzt aktualisiert")}: {lastRefreshed.toLocaleString("de-CH", { dateStyle: "short", timeStyle: "medium" })}</span>}
          <button onClick={loadAll} data-testid="sec-refresh" className="bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-3 rounded-lg text-sm flex items-center gap-2 transition-colors"><RefreshCw className="w-4 h-4" /> {t("Aktualisieren")}</button>
          <button onClick={() => setCreateOpen(true)} data-testid="sec-create-user" className="bg-[#A3E635] hover:bg-[#b6f15a] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 transition-colors"><UserPlus className="w-4 h-4" /> {t("Benutzer anlegen")}</button>
        </div>
      </div>

      {/* Tab-Navigation */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 mb-6 border-b border-white/10" data-testid="sec-tabs">
        {TABS.map((tb) => {
          const active = tab === tb.key;
          const count = tb.key === "team" ? teamUsers.length : tb.key === "clients" ? clientUsers.length : null;
          return (
            <button key={tb.key} onClick={() => changeTab(tb.key)} data-testid={`sec-tab-${tb.key}`}
              className={`relative flex items-center gap-2 px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors rounded-t-lg ${active ? "text-[#A3E635]" : "text-gray-400 hover:text-white"}`}>
              <tb.icon className="w-4 h-4" /> {t(tb.label)}
              {count != null && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-[#A3E635]/15 text-[#A3E635]" : "bg-white/10 text-gray-400"}`}>{count}</span>}
              {active && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#A3E635] rounded-full" />}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab ctx={ctx} />}
      {tab === "team" && <TeamTab ctx={ctx} />}
      {tab === "clients" && <ClientUsersTab ctx={ctx} />}
      {tab === "roles" && <RolesMatrixTab ctx={ctx} />}
      {tab === "assign" && <AssignmentsTab ctx={ctx} />}
      {tab === "audit" && <AuditTab ctx={ctx} />}

      {createOpen && <CreateUserModal t={t} clients={clients} matrix={matrix} onClose={() => setCreateOpen(false)} onDone={(res) => { setCreateOpen(false); loadAll(); if (res?.temp_password) setTempPw({ email: res.user.email, password: res.temp_password, emailSent: res.email_sent }); }} />}
      {editUser && <EditUserModal t={t} user={editUser} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); loadAll(); }} />}
      {roleUser && <RoleModal t={t} user={roleUser} onClose={() => setRoleUser(null)} onDone={() => { setRoleUser(null); loadAll(); }} />}
      {teamRoleUser && <TeamRoleModal t={t} user={teamRoleUser} matrix={matrix} onClose={() => setTeamRoleUser(null)} onDone={() => { setTeamRoleUser(null); loadAll(); }} />}
      {scopeUser && <ClientScopeModal t={t} user={scopeUser} clients={clients} onClose={() => setScopeUser(null)} onDone={() => { setScopeUser(null); loadAll(); }} />}
      {assignUser && <AssignCompanyModal t={t} user={assignUser} clients={clients} onClose={() => setAssignUser(null)} onDone={() => { setAssignUser(null); loadAll(); }} />}
      {rightsUser && <ClientRightsModal t={t} user={rightsUser} onClose={() => setRightsUser(null)} onDone={() => { setRightsUser(null); loadAll(); }} />}
      {confirm && <ConfirmModal t={t} {...confirm} onClose={() => setConfirm(null)} />}
      {tempPw && <TempPwModal t={t} {...tempPw} onClose={() => setTempPw(null)} />}
    </div>
  );
}
